var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useMemo } from 'react';
import { format, parse } from 'date-fns';
import Box from '../Box/BoxBase';
import Icon from '../Icon';
import Loader from '../Loader';
import Modal from '../Modal';
import TextField from '../TextField';
import { TucarTheme } from '../theme';
import { applyDefaultDate, compareTwoDates, defaultMaxDate, defaultMinDate, getCalendarContent, getWeekSelectedWeekLimits, getDefaultDateBuilder, isValidDate, isValidDateString, removeSpecialCharacters, } from './utils';
import { DatePickerWrapperStyled } from './styled';
import Calendar from './Calendar';
const DatePicker = (_a) => {
    var _b, _c;
    var props = __rest(_a, []);
    const { anchorOrigin, baseElement, disabled, disableFuture, disablePast, fullWidth, helperText: helperProp, helperType, inputFormat, isWeekly, desktopMode, label, loading, maxDate: rawMaxDate, minDate: rawMinDate, onChange, openTo, style, shouldDisableDate, shouldDisableMonth, shouldDisableYear, value: rawValue, views, } = props;
    const value = applyDefaultDate(rawValue, null, inputFormat);
    const maxDateProp = applyDefaultDate(rawMaxDate, null, inputFormat);
    const minDateProp = applyDefaultDate(rawMinDate, null, inputFormat);
    const originalValue = value ? format(value, inputFormat) : null;
    const maxDate = applyDefaultDate(maxDateProp, defaultMaxDate, inputFormat);
    const minDate = applyDefaultDate(minDateProp, defaultMinDate, inputFormat);
    const helperText = helperType === 'success' && !helperProp ? 'Fecha ingresada correctamente' : helperProp;
    const inputMask = inputFormat.replace(/[a-zA-Z]/g, '0');
    const validViews = useMemo(() => ['year', 'month', 'date'], []);
    const filteredViews = useMemo(() => (views ? views.filter((view) => validViews.includes(view)) : []), [views, validViews]);
    const today = useMemo(() => new Date(), []);
    const getDefaultView = () => {
        if (filteredViews.includes('year'))
            return 'year';
        return 'month';
    };
    const baseElementRef = useRef(null);
    const [dynamicWidth, setDynamicWidth] = useState(fullWidth ? '100%' : '325px');
    const [debouncedValue, setDebouncedValue] = useState(originalValue);
    const [isCalendarOpen, setCalendarOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedView, setSelectedView] = useState(openTo || getDefaultView());
    const [selectedValues, setSelectedValues] = useState(getDefaultDateBuilder(value));
    const [possibleDates, setPossibleDates] = useState(getCalendarContent(new Date(selectedValues.year, selectedValues.month)));
    const handleSelectedValuesChange = ({ year, month, date, range } = {}) => {
        setSelectedValues((oldValues) => (Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, oldValues), (year !== undefined ? { year } : {})), (month !== undefined ? { month } : {})), (date !== undefined ? { date } : {})), (range !== undefined ? { range } : {}))));
    };
    const handleDisableYear = (year) => {
        if (shouldDisableYear(year))
            return true;
        if (disablePast && year < today.getFullYear())
            return true;
        if (disableFuture && year > today.getFullYear())
            return true;
        if (minDate && year < minDate.getFullYear())
            return true;
        if (maxDate && year > maxDate.getFullYear())
            return true;
        return false;
    };
    const handleDisableMonth = (month, year) => {
        if (shouldDisableMonth(month, year))
            return true;
        if (disablePast && year === today.getFullYear() && month < today.getMonth())
            return true;
        if (disableFuture && year === today.getFullYear() && month > today.getMonth())
            return true;
        if (minDate && year === minDate.getFullYear() && month < minDate.getMonth())
            return true;
        if (maxDate && year === maxDate.getFullYear() && month > maxDate.getMonth())
            return true;
        if (handleDisableYear(year))
            return true;
        return false;
    };
    const handleDisableDate = (date) => {
        if (shouldDisableDate(date))
            return true;
        if (disablePast && compareTwoDates(date, today, 'less'))
            return true;
        if (disableFuture && compareTwoDates(date, today, 'greater'))
            return true;
        if (minDate && compareTwoDates(date, minDate, 'less'))
            return true;
        if (maxDate && compareTwoDates(date, maxDate, 'greater'))
            return true;
        if (handleDisableMonth(date.getMonth(), date.getFullYear()))
            return true;
        return false;
    };
    const handleDateChange = (date) => setDebouncedValue(format(date, inputFormat));
    const handleCancel = () => {
        setSelectedValues(getDefaultDateBuilder(value));
        setDebouncedValue(originalValue);
        setCalendarOpen(false);
    };
    const handleConfirmation = () => {
        onChange(isWeekly ? selectedValues.range : selectedValues.date);
        setCalendarOpen(false);
    };
    useEffect(() => {
        if (baseElementRef.current) {
            setDynamicWidth(fullWidth ? '100%' : `${baseElementRef.current.offsetWidth}px`);
        }
    }, [baseElementRef.current, fullWidth]);
    useEffect(() => {
        const timer = setTimeout(() => {
            if (isValidDateString(debouncedValue, removeSpecialCharacters(inputFormat))) {
                const newDate = parse(debouncedValue, removeSpecialCharacters(inputFormat), new Date());
                handleSelectedValuesChange({
                    year: newDate.getFullYear(),
                    month: newDate.getMonth(),
                    date: newDate,
                });
            }
        }, 1000);
        return () => clearTimeout(timer);
    }, [debouncedValue]);
    useEffect(() => setSelectedView(openTo || getDefaultView()), [views, openTo]);
    useEffect(() => {
        setPossibleDates(() => {
            if (selectedValues.year !== null && selectedValues.month !== null)
                return getCalendarContent(new Date(selectedValues.year, selectedValues.month));
            return getCalendarContent();
        });
    }, [selectedValues.year, selectedValues.month]);
    useEffect(() => {
        const { date: selectedDate } = selectedValues || {};
        if (!selectedDate) {
            if (isWeekly)
                handleSelectedValuesChange({ range: null });
            setDebouncedValue(null);
        }
        if (isValidDate(selectedDate)) {
            if (isWeekly)
                handleSelectedValuesChange({ range: getWeekSelectedWeekLimits(selectedDate) });
            handleDateChange(selectedDate);
        }
    }, [selectedValues.date]);
    useEffect(() => {
        if (['year', 'month'].includes(openTo))
            setIsOpen(true);
    }, [openTo]);
    const renderCalendar = () => (_jsx(Calendar, { anchorOrigin: anchorOrigin, desktopMode: desktopMode, disabled: disabled, filteredViews: filteredViews, handleCancel: handleCancel, handleConfirmation: handleConfirmation, handleDisableDate: handleDisableDate, handleDisableMonth: handleDisableMonth, handleDisableYear: handleDisableYear, inputFormat: inputFormat, isOpen: isOpen, isWeekly: isWeekly, maxDate: maxDate, minDate: minDate, possibleDates: possibleDates, selectedValues: selectedValues, selectedView: selectedView, handleSelectedValuesChange: handleSelectedValuesChange, setSelectedView: setSelectedView, setIsOpen: setIsOpen, setDebouncedValue: setDebouncedValue, value: value, views: views }));
    return (_jsxs(DatePickerWrapperStyled, Object.assign({ dynamicWidth: dynamicWidth, style: style }, { children: [baseElement ? (_jsx(Box, Object.assign({ ref: baseElementRef, onClick: () => setCalendarOpen((previousStatus) => !previousStatus) }, { children: baseElement }))) : (_jsx(TextField, Object.assign({ disabled: disabled, helperText: helperText, helperType: helperType, fullWidth: fullWidth, label: label, mask: isWeekly ? `${inputMask} - ${inputMask}` : inputMask, placeholder: isWeekly ? `${inputFormat} - ${inputFormat}` : inputFormat, onChange: (textValue) => setDebouncedValue(textValue), onFieldClick: () => setCalendarOpen((previousStatus) => !previousStatus), onlyLabel: true, startAdornment: _jsx(Icon, { name: "calendarTodayFilled", color: disabled ? TucarTheme.colors.gray700 : TucarTheme.colors.blue500 }), value: isWeekly
                    ? `${selectedValues.range ? format((_b = selectedValues.range) === null || _b === void 0 ? void 0 : _b.startDate, inputFormat) : ''} - ${selectedValues.range ? format((_c = selectedValues.range) === null || _c === void 0 ? void 0 : _c.endDate, inputFormat) : ''}`.trim()
                    : debouncedValue }, ((!desktopMode || isWeekly) && { readOnly: true }), (desktopMode &&
                !isWeekly && {
                onKeyDown: (event, key) => {
                    if (key === 'Enter') {
                        const dateToSubmit = isValidDateString(debouncedValue, removeSpecialCharacters(inputFormat))
                            ? parse(debouncedValue, removeSpecialCharacters(inputFormat), new Date())
                            : null;
                        if (dateToSubmit && !handleDisableDate(dateToSubmit)) {
                            onChange(dateToSubmit);
                            event.preventDefault();
                        }
                    }
                },
            }), (loading && { endAdornment: _jsx(Loader, { "data-testid": "loading", size: 16 }) })))), isCalendarOpen && desktopMode && renderCalendar(), !desktopMode && (_jsx(Modal, Object.assign({}, (isCalendarOpen ? { 'data-testid': 'calendar-modal' } : {}), { onClose: () => setCalendarOpen(false), open: isCalendarOpen, style: { overflow: 'scroll' } }, { children: renderCalendar() })))] })));
};
DatePicker.defaultProps = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
    disablePast: false,
    disableFuture: false,
    inputFormat: 'dd/MM/yyyy',
    loading: false,
    shouldDisableDate: () => false,
    shouldDisableMonth: () => false,
    shouldDisableYear: () => false,
    views: ['year', 'month', 'date'],
};
export default DatePicker;
//# sourceMappingURL=index.js.map